import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as PreviewIcon,
  Help as HelpIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  AutoAwesome as AIIcon,
  Code as CodeIcon,
  Psychology as PsychologyIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useApi } from '../services/api';
import aiService from '../services/aiService';
import Editor from '@monaco-editor/react';

interface TemplateVariable {
  name: string;
  description: string;
  type: string;
  default?: string;
}

const TemplateCreation: React.FC = () => {
  const { getAuthenticatedApi } = useApi();
  const [mode, setMode] = useState<'manual' | 'ai'>('manual');
  const [templateName, setTemplateName] = useState('');
  const [vendor, setVendor] = useState('extreme');
  const [switchType, setSwitchType] = useState('edge');
  const [version, setVersion] = useState('1.0');
  const [description, setDescription] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [helpDialog, setHelpDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  
  // AI Mode states
  const [aiDescription, setAiDescription] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult, setAiResult] = useState<any>(null);
  const [confidenceScore, setConfidenceScore] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [missingParams, setMissingParams] = useState<string[]>([]);

  const commonVariables: TemplateVariable[] = [
    { name: 'switch_name', description: 'Hostname of the switch', type: 'string' },
    { name: 'switch_location', description: 'Physical location', type: 'string' },
    { name: 'switch_ip', description: 'Management IP address', type: 'string' },
    { name: 'port_count', description: 'Number of ports', type: 'number', default: '48' },
    { name: 'default_route', description: 'Default gateway IP', type: 'string' },
    { name: 'radius_enabled', description: 'Enable RADIUS', type: 'boolean', default: 'false' },
    { name: 'radius_secret', description: 'RADIUS shared secret', type: 'string' },
    { name: 'custom_vlans', description: 'List of custom VLANs', type: 'array' },
    { name: 'is_primary', description: 'Primary in HA pair', type: 'boolean', default: 'true' },
    { name: 'vrrp_config', description: 'VRRP configuration', type: 'object' }
  ];

  const aiExamples = [
    'Create an edge switch template for 48 ports with standard VLANs and RADIUS authentication',
    'Generate a core switch template with VRRP for high availability and redundant uplinks',
    'Build a distribution switch template with QoS, custom VLANs, and spanning tree configuration',
    'Design a campus edge switch template with 802.1X authentication and DHCP snooping'
  ];

  const templateExamples = {
    cisco: `! Cisco Configuration Template
hostname {{ switch_name }}
!
! Management Interface
interface Vlan{{ management_vlan | default(1) }}
 description Management VLAN
 ip address {{ switch_ip }} {{ management_mask }}
 no shutdown
!
ip default-gateway {{ default_gateway }}
!
{% if radius_enabled %}
! RADIUS Configuration
radius server PRIMARY
 address ipv4 {{ radius_server }}
 key {{ radius_secret }}
{% endif %}`,
    extreme: `# Extreme Networks Configuration Template
configure snmp sysName "{{ switch_name }}"
configure snmp sysLocation "{{ switch_location }}"

# Management VLAN
create vlan "Mgmt"
configure vlan Mgmt tag {{ management_vlan | default(1) }}
configure vlan Mgmt ipaddress {{ switch_ip }} {{ management_mask }}

# Default Route
configure iproute add default {{ default_route }}

{% if radius_enabled %}
# RADIUS Configuration
configure radius primary server {{ radius_server }} 1812 client-ip {{ switch_ip }}
configure radius primary shared-secret {{ radius_secret }}
{% endif %}`
  };

  useEffect(() => {
    if (mode === 'manual') {
      extractVariables();
    }
  }, [templateContent, mode]);

  const extractVariables = () => {
    const regex = /\{\{\s*(\w+)(?:\s*\|\s*default\(['"](.*?)['"]\))?\s*\}\}/g;
    const foundVars = new Set<string>();
    let match;
    
    while ((match = regex.exec(templateContent)) !== null) {
      foundVars.add(match[1]);
    }
    
    const varList = Array.from(foundVars).map(varName => {
      const existing = commonVariables.find(v => v.name === varName);
      return existing || { name: varName, description: '', type: 'string' };
    });
    
    setVariables(varList);
  };

  const handleModeChange = (event: React.MouseEvent<HTMLElement>, newMode: 'manual' | 'ai' | null) => {
    if (newMode !== null) {
      setMode(newMode);
      setError(null);
      setSuccess(null);
    }
  };

  const handleAIGenerate = async () => {
    const validation = aiService.validateDescription(aiDescription);
    if (!validation.isValid) {
      setError(validation.message!);
      return;
    }

    setAiGenerating(true);
    setError(null);
    setSuggestions([]);
    setMissingParams([]);

    try {
      const result = await aiService.generateTemplate({
        description: aiDescription,
        vendor,
        switch_type: switchType,
        template_name: templateName || undefined,
        save_to_database: false
      });

      setAiResult(result);
      setTemplateContent(result.template_content);
      setConfidenceScore(result.confidence_score);
      setSuggestions(result.suggestions);
      setMissingParams(result.missing_parameters);
      
      // Extract variables from generated template
      extractVariables();

      if (result.confidence_score < 0.6) {
        setError('Low confidence in generated template. Please review carefully.');
      } else if (result.missing_parameters.length > 0) {
        setError(`Missing parameters: ${result.missing_parameters.join(', ')}`);
      } else {
        setSuccess('Template generated successfully!');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to generate template with AI');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName || !templateContent) {
      setError('Please provide template name and content');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const api = await getAuthenticatedApi();
      
      // If AI-generated, include AI metadata
      const templateData: any = {
        name: templateName,
        vendor,
        switch_type: switchType,
        version,
        description: description || (mode === 'ai' ? `AI-generated from: ${aiDescription}` : ''),
        template_content: templateContent
      };

      if (mode === 'ai' && aiResult) {
        templateData.ai_generated = true;
        templateData.confidence_score = confidenceScore;
        templateData.parameter_schema = aiResult.parameter_schema;
        templateData.supported_features = aiResult.supported_features;
      }

      await api.createTemplate(templateData);
      
      setSuccess('Template created successfully!');
      // Reset form
      setTemplateName('');
      setDescription('');
      setTemplateContent('');
      setAiDescription('');
      setVersion('1.0');
      setAiResult(null);
      setConfidenceScore(null);
    } catch (error: any) {
      setError(error.message || 'Failed to create template');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    if (!templateContent) {
      setError('Please add template content first');
      return;
    }
    setPreviewDialog(true);
  };

  const handleCopyExample = () => {
    if (mode === 'ai') {
      const randomExample = aiExamples[Math.floor(Math.random() * aiExamples.length)];
      setAiDescription(randomExample);
    } else {
      setTemplateContent(templateExamples[vendor as keyof typeof templateExamples]);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getConfidenceDisplay = () => {
    if (!confidenceScore) return null;
    const interpretation = aiService.interpretConfidenceScore(confidenceScore);
    return (
      <Alert severity={interpretation.color} sx={{ mt: 2 }}>
        <strong>{interpretation.label}</strong> - {interpretation.description}
      </Alert>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Template Creation
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Create reusable templates manually or with AI assistance
      </Typography>

      {/* Mode Toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="subtitle1">Creation Mode:</Typography>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={handleModeChange}
            aria-label="creation mode"
          >
            <ToggleButton value="manual" aria-label="manual mode">
              <CodeIcon sx={{ mr: 1 }} />
              Manual
            </ToggleButton>
            <ToggleButton value="ai" aria-label="ai mode">
              <AIIcon sx={{ mr: 1 }} />
              AI Assistant
            </ToggleButton>
          </ToggleButtonGroup>
          {mode === 'ai' && (
            <Chip
              icon={<PsychologyIcon />}
              label="AI-Powered"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Stack spacing={3}>
              <TextField
                label="Template Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                fullWidth
                required
                placeholder="e.g., Extreme Core Switch HA Template"
              />

              <Grid container spacing={2}>
                <Grid size={4}>
                  <TextField
                    select
                    label="Vendor"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="extreme">Extreme Networks</MenuItem>
                    <MenuItem value="cisco">Cisco</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={4}>
                  <TextField
                    select
                    label="Switch Type"
                    value={switchType}
                    onChange={(e) => setSwitchType(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="edge">Edge</MenuItem>
                    <MenuItem value="core">Core</MenuItem>
                    <MenuItem value="distribution">Distribution</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={4}>
                  <TextField
                    label="Version"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    fullWidth
                    placeholder="1.0"
                  />
                </Grid>
              </Grid>

              {mode === 'ai' ? (
                <>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Describe Your Template Requirements
                    </Typography>
                    <TextField
                      label="Natural Language Description"
                      value={aiDescription}
                      onChange={(e) => setAiDescription(e.target.value)}
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Describe the template you want to create. For example: Create a core switch template with VRRP for high availability, supporting 48 ports with custom VLANs and RADIUS authentication..."
                      helperText="Be specific about features, requirements, and configurations you need"
                    />
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<AIIcon />}
                      onClick={handleAIGenerate}
                      disabled={aiGenerating || !aiDescription}
                      color="primary"
                    >
                      Generate with AI
                    </Button>
                    <Button
                      size="small"
                      startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                      onClick={handleCopyExample}
                    >
                      {copied ? 'Copied!' : 'Use Example'}
                    </Button>
                  </Stack>

                  {aiGenerating && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        AI is generating your template...
                      </Typography>
                      <LinearProgress />
                    </Box>
                  )}

                  {getConfidenceDisplay()}

                  {suggestions.length > 0 && (
                    <Alert severity="info">
                      <Typography variant="subtitle2" gutterBottom>
                        AI Suggestions:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {suggestions.map((suggestion, idx) => (
                          <li key={idx}>{suggestion}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {missingParams.length > 0 && (
                    <Alert severity="warning">
                      <Typography variant="subtitle2" gutterBottom>
                        Missing Parameters:
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {missingParams.map((param) => (
                          <Chip
                            key={param}
                            label={param}
                            size="small"
                            icon={<WarningIcon />}
                            color="warning"
                          />
                        ))}
                      </Stack>
                    </Alert>
                  )}
                </>
              ) : (
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Describe what this template does..."
                />
              )}

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1">
                    Template Content (Jinja2)
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {mode === 'manual' && (
                      <Button
                        size="small"
                        startIcon={copied ? <CheckIcon /> : <CopyIcon />}
                        onClick={handleCopyExample}
                      >
                        {copied ? 'Copied!' : 'Use Example'}
                      </Button>
                    )}
                    <IconButton size="small" onClick={() => setHelpDialog(true)}>
                      <HelpIcon />
                    </IconButton>
                  </Stack>
                </Box>
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Editor
                    height="400px"
                    defaultLanguage="jinja2"
                    value={templateContent}
                    onChange={(value) => setTemplateContent(value || '')}
                    theme="vs-dark"
                    options={{
                      minimap: { enabled: false },
                      fontSize: 14,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      scrollBeyondLastLine: false,
                      readOnly: mode === 'ai' && aiGenerating
                    }}
                  />
                </Box>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveTemplate}
                  disabled={loading || !templateName || !templateContent}
                >
                  Save Template
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  disabled={!templateContent}
                >
                  Preview
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detected Variables
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Variables found in your template:
              </Typography>
              {variables.length > 0 ? (
                <Stack spacing={1}>
                  {variables.map((variable) => (
                    <Box key={variable.name}>
                      <Chip
                        label={variable.name}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                      {variable.description && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {variable.description}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No variables detected yet
                </Typography>
              )}
            </CardContent>
          </Card>

          {mode === 'ai' && aiResult && (
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  AI Analysis
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Supported Features:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      {Object.entries(aiResult.supported_features || {})
                        .filter(([, supported]) => supported)
                        .map(([feature]) => (
                          <Chip
                            key={feature}
                            label={feature.replace('_', ' ')}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {mode === 'ai' ? 'AI Tips' : 'Quick Tips'}
              </Typography>
              {mode === 'ai' ? (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Be specific"
                      secondary="Include details about ports, VLANs, and features"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Mention security"
                      secondary="Specify RADIUS, SSH, or other security needs"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="HA requirements"
                      secondary="For core switches, mention VRRP or redundancy"
                    />
                  </ListItem>
                </List>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Use {{ variable }}"
                      secondary="To insert a variable"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="{% if condition %}"
                      secondary="For conditional sections"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="{% for item in list %}"
                      secondary="To loop through items"
                    />
                  </ListItem>
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onClose={() => setPreviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Preview</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This shows the raw template. Variables will be replaced with actual values during generation.
          </Alert>
          <Box sx={{ p: 2, bgcolor: 'grey.900', borderRadius: 1 }}>
            <pre style={{ 
              color: '#00ff00', 
              margin: 0, 
              whiteSpace: 'pre-wrap', 
              wordWrap: 'break-word',
              fontSize: '0.875rem'
            }}>
              {templateContent}
            </pre>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialog} onClose={() => setHelpDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Template Guide</DialogTitle>
        <DialogContent>
          {mode === 'ai' ? (
            <>
              <Typography variant="h6" gutterBottom>AI Description Examples</Typography>
              <List>
                {aiExamples.map((example, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={`Example ${idx + 1}`}
                      secondary={example}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>Jinja2 Template Syntax</Typography>
              <List>
                <ListItem>
                  <ListItemText
                    primary={<code>{'{{ variable_name }}'}</code>}
                    secondary="Replace with the value of variable_name"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={<code>{'{{ variable | default("fallback") }}'}</code>}
                    secondary="Use fallback value if variable is not defined"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={<code>{'{% if condition %} ... {% endif %}'}</code>}
                    secondary="Conditional block"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={<code>{'{% for item in list %} ... {% endfor %}'}</code>}
                    secondary="Loop through items"
                  />
                </ListItem>
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateCreation;